import { useCallback, useEffect } from "react";

interface UseVisibilityChangeProps {
  onVisible: () => void;
  onHidden: () => void;
}

/**
 * If a user exits the current tab view, execute the onHidden callback.
 * If the user comes back into view, call the onVisible callback.
 */
const useVisibilityChange = ({
  onVisible,
  onHidden,
}: UseVisibilityChangeProps): void => {
  const onVisibilityChange = useCallback(() => {
    // if a user is not focused on our app, stop polling
    if (document.hidden) {
      onHidden();
    } else {
      // if a user is re-focuses on our app, start polling
      onVisible();
    }
  }, [onHidden, onVisible]);

  useEffect(() => {
    // very old browsers do not support page visibility API
    if (document?.hidden !== undefined) {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }

    return () => {
      if (document?.hidden !== undefined) {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
    };
  }, []);

  return;
};

export default useVisibilityChange;
